import type {
  BusinessReportPrompt,
  FamilySummaryMode,
  PromptAutoRefreshFrequency,
  PromptDataType,
} from "@/modules/snapshots/businessReports.types";

export interface PromptEditFormInputs {
  name: string;
  prompt: string;
  full_context: boolean;
  is_date: boolean;
  data_type: PromptDataType | null;
  use_image: boolean;
  no_retriever: boolean;
  web_search: boolean;
  traffic_light: boolean;
  auto_refresh_frequency: PromptAutoRefreshFrequency | null;
  family_summary_mode: FamilySummaryMode;
  family_summary_prompt: string | null;
  family_metadata: boolean;
}

export const FAMILY_SUMMARY_MODE_OPTIONS: ReadonlyArray<{ value: FamilySummaryMode; label: string }> = [
  { value: "summarise", label: "Summarise with a prompt" },
  { value: "copy_master", label: "Copy the master document's value" },
  { value: "blank", label: "Leave empty (-)" },
];

/** Prompts saved before the explicit mode existed: a prompt meant "summarise", none meant "copy the master". */
export const getFamilySummaryMode = (
  prompt: Pick<BusinessReportPrompt, "family_summary_mode" | "family_summary_prompt">,
): FamilySummaryMode =>
  prompt.family_summary_mode ?? (prompt.family_summary_prompt?.trim() ? "summarise" : "copy_master");

export const getPromptEditDefaultValues = (prompt: BusinessReportPrompt): PromptEditFormInputs => ({
  name: prompt.name,
  prompt: prompt.prompt,
  full_context: prompt.full_context,
  is_date: prompt.is_date,
  data_type: getPromptDataType(prompt),
  use_image: prompt.use_image,
  no_retriever: prompt.no_retriever,
  web_search: prompt.web_search,
  traffic_light: prompt.traffic_light ?? false,
  auto_refresh_frequency: prompt.auto_refresh_frequency ?? null,
  family_summary_mode: getFamilySummaryMode(prompt),
  family_summary_prompt: prompt.family_summary_prompt ?? null,
  family_metadata: prompt.family_metadata ?? false,
});

export const getPromptDataType = (prompt: BusinessReportPrompt): PromptDataType | null => {
  if (prompt.data_type) {
    return prompt.data_type;
  }

  return prompt.is_date ? "date" : null;
};

export const normalizePromptEditPayloadForSubmit = (
  payload: PromptEditFormInputs,
  isBusinessReport: boolean,
): PromptEditFormInputs => {
  // The consolidation prompt only exists for "summarise"; the other modes never call the model.
  const familySummaryMode: FamilySummaryMode = isBusinessReport ? "copy_master" : payload.family_summary_mode;
  payload = {
    ...payload,
    family_summary_mode: familySummaryMode,
    family_summary_prompt: familySummaryMode === "summarise" ? payload.family_summary_prompt?.trim() || null : null,
    family_metadata: isBusinessReport ? false : payload.family_metadata,
  };
  if (payload.web_search) {
    payload = {
      ...payload,
      full_context: false,
      use_image: false,
      no_retriever: false,
      auto_refresh_frequency: null,
    };
  }
  if (isBusinessReport || payload.traffic_light) {
    return {
      ...payload,
      data_type: "categorical",
      is_date: false,
      web_search: isBusinessReport ? false : payload.web_search,
      auto_refresh_frequency: isBusinessReport ? null : payload.auto_refresh_frequency,
    };
  }

  return {
    ...payload,
    is_date: payload.data_type === "date",
  };
};
